import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';
import {
  trackCtaTryDemoClicked,
  trackCtaBookCallClicked,
  setupScrollDepthTracking,
  setupTimeOnPageTracking
} from '../utils/analytics';

const LandingPage = () => {
  const navigate = useNavigate();

  // ROI Calculator State
  const [patientsPerDay, setPatientsPerDay] = React.useState(6);
  const [minutesPerNote, setMinutesPerNote] = React.useState(10);
  const [hourlyRate, setHourlyRate] = React.useState(70);

  // Calculate ROI metrics
  const calculateROI = () => {
    const timeSavedPerNoteMinutes = minutesPerNote * 0.7; // 70% time reduction
    const timeSavedPerDay = (timeSavedPerNoteMinutes * patientsPerDay) / 60; // in hours
    const moneySavedPerDay = timeSavedPerDay * hourlyRate;
    const moneySavedPerWeek = moneySavedPerDay * 5; // 5 working days per week
    const moneySavedPerYear = moneySavedPerWeek * 52; // 52 weeks per year
    const hoursSavedPerYear = timeSavedPerDay * 5 * 52;

    return {
      timeSavedPerDay: timeSavedPerDay.toFixed(1),
      moneySavedPerDay: Math.round(moneySavedPerDay),
      moneySavedPerWeek: Math.round(moneySavedPerWeek),
      moneySavedPerYear: Math.round(moneySavedPerYear),
      hoursSavedPerYear: Math.round(hoursSavedPerYear)
    };
  };

  const roiMetrics = calculateROI();

  useEffect(() => {
    // Smooth scroll behavior
    document.documentElement.style.scrollBehavior = 'smooth';

    // Setup analytics tracking
    const cleanupScrollTracking = setupScrollDepthTracking();
    const cleanupTimeTracking = setupTimeOnPageTracking('landing');

    // Intersection Observer for fade-in animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -100px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('lp-fade-in-visible');
        }
      });
    }, observerOptions);

    const fadeElements = document.querySelectorAll('.lp-fade-in');
    fadeElements.forEach(el => observer.observe(el));

    // Navbar background on scroll
    const handleScroll = () => {
      const navbar = document.querySelector('.lp-navbar');
      if (window.scrollY > 200) {
        navbar.classList.add('lp-navbar-scrolled');
      } else {
        navbar.classList.remove('lp-navbar-scrolled');
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      fadeElements.forEach(el => observer.unobserve(el));
      window.removeEventListener('scroll', handleScroll);
      document.documentElement.style.scrollBehavior = 'auto';
      cleanupScrollTracking();
      cleanupTimeTracking();
    };
  }, []);

  const handleBookDemo = (location = 'hero') => {
    trackCtaBookCallClicked(location);
    window.Calendly.initPopupWidget({ url: 'https://calendly.com/algodentra/30min' });
    return false;
  };

  const handleTryDemo = (location = 'hero') => {
    trackCtaTryDemoClicked(location);
    navigate('/demo');
  };

  const pipelineStages = [
    {
      title: 'Stage 1 · Local Transcription',
      description: 'Our model processes audio entirely on your clinic hardware while adaptive noise suppression cuts through dental drills, waiting-room chatter, and equipment hums for crystal-clear transcripts—no cloud relay, no third-party servers.'
    },
    {
      title: 'Stage 2 · Local PII Redaction',
      description: 'Automated regex plus Microsoft Presidio scrubs patient names, phone numbers, addresses, and health card identifiers entirely on-device—no cloud intervention and zero third-party API calls ever see raw PHI.'
    },
    {
      title: 'Stage 3 · Secure Claude Anthropic Processing',
      description: 'Only redacted, de-identified transcripts are sent to Anthropic Claude for intelligent SOAP structuring, CDA billing codes, clinical recommendations, and patient education—zero raw PHI exposure.'
    },
    {
      title: 'Stage 4 · Transparent Patient Portal',
      description: 'Every patient gets a secure portal the moment their first record is ready—no summaries lost in inboxes or buried in systems. They receive encrypted email access to their appointment summaries and SOAP notes that remain available for as long as they choose, keeping transparency and patient peace of mind front and center.'
    }
  ];

  const capabilities = [
    {
      title: 'Chairside Audio Capture',
      description: 'Record consultations in-browser or upload MP3, WAV, M4A, WEBM files. Built-in duration validation and audio quality checks ensure clean transcription every time.',
      icon: (
        <svg width="46" height="46" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 14C13.6569 14 15 12.6569 15 11V5C15 3.34315 13.6569 2 12 2C10.3431 2 9 3.34315 9 5V11C9 12.6569 10.3431 14 12 14Z" stroke="currentColor" strokeWidth="1.8"/>
          <path d="M5 11V12C5 15.3137 7.68629 18 11 18H13C16.3137 18 19 15.3137 19 12V11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
        </svg>
      )
    },
    {
      title: 'Compliance That’s Built In, Not Bolted On',
      description: 'Algodentra scrubs PII before the cloud ever sees a byte, encrypts every shred of PHI with Fernet-grade security, and tracks every action with full WHO/WHAT/WHEN/OUTCOME audit logs.',
      icon: (
        <svg width="46" height="46" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 22C12 22 20 18 20 12V5L12 2L4 5V12C4 18 12 22 12 22Z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      title: 'AI-Powered Clinical Intelligence',
      description: 'Powered by Anthropic Claude 4.5, Algodentra transforms raw chairside conversations into smart, structured SOAP notes with clinical depth. It automatically suggests CDA billing codes, surfaces decision-support insights, and even generates patient-friendly education tips — all in one seamless workflow.',
      icon: (
        <svg width="46" height="46" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M7 3H17L21 7V21C21 21.5523 20.5523 22 20 22H7V3Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
          <path d="M7 3H5C4.44772 3 4 3.44772 4 4V21C4 21.5523 4.44772 22 5 22H7" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      title: 'CDA Billing Intelligence',
      description: 'Automatic CDA billing code suggestions, clinical decision support, patient education content, follow-up recommendations, and risk flagging—all contextualized to your treatment plan.',
      icon: (
        <svg width="46" height="46" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C6.47715 2 2 6.477 2 12C2 17.5229 6.47715 22 12 22C17.5228 22 22 17.5229 22 12C22 6.477 17.5228 2 12 2Z" stroke="currentColor" strokeWidth="1.8"/>
          <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    {
      title: 'Encrypted, Role-Based Access',
      description: 'Fernet encryption at rest with rotating keys. Doctors see only their own patients. Patients access their records through secure JWT authentication. Zero cross-contamination.',
      icon: (
        <svg width="46" height="46" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 20C5 16.6863 7.68629 14 11 14H13C16.3137 14 19 16.6863 19 20" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          <path d="M12 12C13.6569 12 15 10.6569 15 9C15 7.34315 13.6569 6 12 6C10.3431 6 9 7.34315 9 9C9 10.6569 10.3431 12 12 12Z" stroke="currentColor" strokeWidth="1.8"/>
        </svg>
      )
    },
    {
      title: 'Patient Portal Access (On Any Device, Anytime)',
      description: 'Give patients a secure, modern way to own their records. With encrypted email verification, token-based password resets, and a fully mobile-responsive portal, patients can safely view their medical records from anywhere — without your team lifting a finger.',
      icon: (
     <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="5" y="2" width="14" height="20" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <line x1="12" y1="18" x2="12" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>

      )
    }
  ];

  const workflowSteps = [
    {
      title: 'Record Your Consultation',
      description: 'Use in-browser recording or upload existing audio from your phone. Algodentra validates duration and format automatically—no technical expertise required.'
    },
    {
      title: 'Local Processing & Redaction',
      description: 'Faster-Whisper transcribes on your device, then Microsoft Presidio and regex scrub every identifier locally. Your raw audio and patient identifiers never touch external servers.'
    },
    {
      title: 'AI Documentation & Insights',
      description: 'Sanitized transcripts generate structured SOAP notes, CDA billing codes, clinical recommendations, patient education materials, and follow-up protocols in under 60 seconds.'
    },
    {
      title: 'Encrypted Storage & Sharing',
      description: 'Save to Fernet-encrypted database with role-based access control. Export to your existing EHR or share securely with patients through their dedicated portal.'
    }
  ];

  const complianceHighlights = [
    { title: 'Privacy-First, BAA Pending', description: 'Local processing, PII redaction, audit logging, and encryption at rest. Formal HIPAA/PIPEDA/PHIPA compliance will follow once we deploy to BAA-backed infrastructure.' },
    { title: 'Zero-Cloud Audio Storage', description: 'Audio files are transcribed and redacted on your local hardware before any cloud interaction. No audio ever leaves your clinic unprocessed.' },
    { title: 'Granular Role Permissions', description: 'JWT-based authentication with scoped access. Doctors view only their patients and patients see only their own records—no broad admin layer touching clinical data.' },
    { title: 'Military-Grade Encryption', description: 'Fernet symmetric encryption at rest with rotating keys. Envelope encryption ensures each SOAP note, transcript, and audio file has unique protection.' }
  ];

  return (
    <div className="landing-page">
      {/* Navigation Bar */}
      <nav className="lp-navbar">
        <div className="lp-container">
          <div className="lp-nav-content">
            <div className="lp-logo">
              <svg width="120" height="60" viewBox="0 0 100 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g>
                  <path d="M10 30 L20 10 L30 30 Z" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="38" y1="20" x2="38" y2="20" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                  <line x1="43" y1="15" x2="43" y2="25" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                  <line x1="48" y1="10" x2="48" y2="30" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                  <line x1="53" y1="12" x2="53" y2="28" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                  <line x1="58" y1="18" x2="58" y2="22" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                  <line x1="63" y1="14" x2="63" y2="26" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                  <line x1="68" y1="16" x2="68" y2="24" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                  <line x1="73" y1="20" x2="73" y2="20" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                </g>
              </svg>
            </div>
            <button className="lp-login-button" onClick={() => handleTryDemo('navbar')}>
              Try Demo
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="lp-hero-section">
        <div className="lp-container">
          <div className="lp-hero-grid">
            <div className="lp-hero-copy lp-fade-in">
              <div className="lp-eyebrow">Algodentra</div>
              <h1>Smart, secure dental notes for Canada.</h1>
              <p>
                Algodentra uses advanced artificial intelligence to instantly create accurate SOAP notes and dental billing codes, allowing your practice to run more efficiently with fewer errors. Spend less time documenting and more time caring for your patients. Experience the clinical assistant designed specifically for dentists.
                 </p>
              <div className="lp-hero-actions">
                <button className="lp-cta-button lp-primary" onClick={() => handleBookDemo('hero')}>
                  Book a Call
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button className="lp-cta-button lp-ghost" onClick={() => handleTryDemo('hero')}>
                  Try Demo
                </button>
              </div>
              <div className="lp-hero-metrics">
                <div>
                  <span>60s</span>
                  <p>Average time to complete SOAP</p>
                </div>
                <div>
                  <span>2</span>
                  <p>Local stages before Anthropic</p>
                </div>
                <div>
                  <span>0</span>
                  <p>Unredacted PHI sent to cloud</p>
                </div>
              </div>
            </div>
            <div className="lp-hero-video lp-fade-in">
              <div className="lp-hero-video-frame">
                <div className="lp-hero-video-badge">Live product demo</div>
                <video
                  className="lp-hero-video-player"
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  poster="/demo-video-poster.jpg"
                >
                  <source src="/demo-video.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
              <div className="lp-hero-pipeline-card">
                <div className="lp-hero-pipeline-header">
                  <div className="lp-hero-pipeline-title">Clinical Automation Pipeline</div>
                  <div className="lp-hero-pipeline-status">Privacy-first · BAA pending</div>
                </div>
                <ul>
                  {pipelineStages.map(stage => (
                    <li key={stage.title} className="lp-hero-stage">
                      <strong>{stage.title}</strong>
                      <span>{stage.description}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="lp-section lp-capabilities-section">
        <div className="lp-container">
          <div className="lp-section-header lp-fade-in">
            <h2 className="lp-section-title">Everything you need to document faster</h2>
            <p className="lp-section-description">Transcription, compliance, SOAP automation, and delivery handled in one secure flow.</p>
          </div>
          <div className="lp-capabilities-grid">
            {capabilities.map(cap => (
              <div className="lp-capability-card lp-fade-in" key={cap.title}>
                <div className="lp-capability-icon">{cap.icon}</div>
                <h3>{cap.title}</h3>
                <p>{cap.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="lp-section lp-workflow-section">
        <div className="lp-container">
          <div className="lp-workflow-card lp-fade-in">
            <div className="lp-section-header">
              <h2 className="lp-section-title">A frictionless workflow from chairside to chart</h2>
              <p className="lp-section-description">Algodentra mirrors your clinical routine with automated guardrails.</p>
            </div>
            <div className="lp-workflow-timeline">
              {workflowSteps.map((step, index) => (
                <div className="lp-workflow-step lp-fade-in" key={step.title}>
                  <div className="lp-step-index">{index + 1}</div>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator */}
      <section className="lp-section lp-roi-section">
        <div className="lp-container">
          <div className="lp-roi-card lp-fade-in">
            <div className="lp-section-header">
              <h2 className="lp-section-title">Calculate your time savings</h2>
              <p className="lp-section-description">See how much time and money Algodentra can save your practice.</p>
            </div>

            <div className="lp-roi-content">
              {/* Input Section */}
              <div className="lp-roi-inputs">
                <div className="lp-roi-input-group">
                  <label className="lp-roi-label">
                    How many patient appointments do you see per day?
                  </label>
                  <input
                    type="number"
                    className="lp-roi-input"
                    value={patientsPerDay}
                    onChange={(e) => setPatientsPerDay(e.target.value === '' ? '' : parseInt(e.target.value))}
                    onBlur={(e) => setPatientsPerDay(Math.max(1, parseInt(e.target.value) || 1))}
                    min="1"
                    max="100"
                  />
                </div>

                <div className="lp-roi-input-group">
                  <label className="lp-roi-label">
                    How many minutes do you spend on notes per appointment?
                  </label>
                  <div className="lp-roi-slider-container">
                    <input
                      type="range"
                      className="lp-roi-slider"
                      value={minutesPerNote}
                      onChange={(e) => setMinutesPerNote(parseInt(e.target.value))}
                      min="1"
                      max="30"
                    />
                    <span className="lp-roi-slider-value">{minutesPerNote} min</span>
                  </div>
                </div>

                <div className="lp-roi-input-group">
                  <label className="lp-roi-label">
                    What do you value your time at?
                  </label>
                  <div className="lp-roi-currency-input">
                    <span className="lp-roi-currency-symbol">$</span>
                    <input
                      type="number"
                      className="lp-roi-input lp-roi-input-currency"
                      value={hourlyRate}
                      onChange={(e) => setHourlyRate(e.target.value === '' ? '' : parseInt(e.target.value))}
                      onBlur={(e) => setHourlyRate(Math.max(1, parseInt(e.target.value) || 1))}
                      min="1"
                      max="1000"
                    />
                    <span className="lp-roi-currency-unit">/hour</span>
                  </div>
                </div>
              </div>

              {/* Results Section */}
              <div className="lp-roi-results">
                <div className="lp-roi-results-header">
                  <h3>Your Potential Savings</h3>
                  <p className="lp-roi-assumption">Based on 70% time reduction per note</p>
                </div>

                <div className="lp-roi-metrics">
                  <div className="lp-roi-metric lp-roi-metric-primary">
                    <div className="lp-roi-metric-value">${roiMetrics.moneySavedPerYear.toLocaleString()}</div>
                    <div className="lp-roi-metric-label">Missed out yearly</div>
                  </div>

                  <div className="lp-roi-metric-grid">
                    <div className="lp-roi-metric lp-roi-metric-secondary">
                      <div className="lp-roi-metric-value">{roiMetrics.timeSavedPerDay}h</div>
                      <div className="lp-roi-metric-label">Lost per day</div>
                    </div>

                    <div className="lp-roi-metric lp-roi-metric-secondary">
                      <div className="lp-roi-metric-value">${roiMetrics.moneySavedPerWeek.toLocaleString()}</div>
                      <div className="lp-roi-metric-label">Missed out weekly</div>
                    </div>

                    <div className="lp-roi-metric lp-roi-metric-secondary">
                      <div className="lp-roi-metric-value">{roiMetrics.hoursSavedPerYear}h</div>
                      <div className="lp-roi-metric-label">Hours lost with family yearly</div>
                    </div>
                  </div>
                </div>

                <div className="lp-roi-cta">
                  <button className="lp-cta-button lp-primary" onClick={() => handleTryDemo('roi_calculator')}>
                    See It In Action
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="lp-section lp-compliance-section">
        <div className="lp-container">
          <div className="lp-section-header lp-fade-in">
            <h2 className="lp-section-title">Security engineered for healthcare</h2>
            <p className="lp-section-description">Privacy is built into every layer, from recording to storage.</p>
          </div>
          <div className="lp-compliance-grid">
            {complianceHighlights.map(item => (
              <div className="lp-compliance-card lp-fade-in" key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="lp-section lp-cta-section">
        <div className="lp-cta-content lp-fade-in">
          <h2>See Algodentra in action</h2>
          <p>Walk through the product with our team and see how privacy-first documentation works (formal HIPAA/BAA compliance pending deployment).</p>
          <div className="lp-hero-actions">
            <button className="lp-cta-button lp-primary" onClick={() => handleBookDemo('cta_section')}>
              Book a Call
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

          </div>
        </div>
      </section>

      <footer className="lp-footer">
        <div className="lp-container">
          <p>&copy; 2026 Algodentra. All rights reserved.</p>
          <p className="lp-footer-tagline">Secure, private documentation — full HIPAA/BAA alignment pending future deployment</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
